import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import {
  Dashboard,
  Overview,
  ProcessedEvents,
  Role,
  ListZero,
  ListZeroWorkflow,
  ListZeroStatuses,
  ListZeroScenarios,
  ListZeroFields,
  ListZeroParticipants,
  ListOne,
  ListOneTicketDetails,
  ListOneTicketStudies,
  ListOneStatusesListing,
  ListTwo,
  ListTwoTicketDetails,
  ListTwoTicketStudies,
  ListThree,
  ListThreeDetails,
  ListFour,
  ListFiveLayout,
  ListFiveNeedForApprovalListing,
  ListFiveOtherInvoicesListing,
  ListSix,
  HistoryLogs,
  Logout,
  ResetPin,
  NewPinResetForm,
  Admin,
  CargoClaims,
  CargoClaimDetails,
  CargoClaimsOverview,
  CargoClaimsStatuses,
  CargoClaimsFields,
  HealthClaims,
  DirectClaims,
  GlobalClaims,
  GlobalApprovals,
  ClosedTickets,
  DocsComingSoon,
} from 'modules';
import { NavigationLayout } from 'layouts';

import { routes } from 'constants/index';

// Sends an old address to its new one, keeping the filters in the query.
function RedirectTo({ path }: { path: string }) {
  const { search } = useLocation();

  return <Navigate replace to={{ pathname: path, search }} />;
}

export function AzadeaRoutes() {
  return (
    <Routes>
      <Route element={<NavigationLayout />}>
        <Route path={routes.main} element={<Dashboard />} />
        <Route path={routes.overview} element={<Overview />} />

        <Route path={routes.listZero.index}>
          <Route index element={<ListZero />} />

          <Route
            path={routes.listZero.participants}
            element={<ListZeroParticipants />}
          />

          {/* Docs moved to /docs/* — keep old links working. */}
          <Route
            path={routes.docs.workflow}
            element={
              <Navigate
                replace
                to={`${routes.docs.index}/${routes.docs.workflow}`}
              />
            }
          />
          <Route
            path={routes.docs.statuses}
            element={
              <Navigate
                replace
                to={`${routes.docs.index}/${routes.docs.statuses}`}
              />
            }
          />
          <Route
            path={routes.docs.scenarios}
            element={
              <Navigate
                replace
                to={`${routes.docs.index}/${routes.docs.scenarios}`}
              />
            }
          />
        </Route>

        <Route path={routes.docs.index}>
          <Route
            index
            element={
              <Navigate
                replace
                to={`${routes.docs.index}/${routes.docs.workflow}`}
              />
            }
          />

          <Route path={routes.docs.workflow} element={<ListZeroWorkflow />} />

          <Route path={routes.docs.statuses} element={<ListZeroStatuses />} />

          <Route path={routes.docs.scenarios} element={<ListZeroScenarios />} />

          <Route path={routes.docs.fields} element={<ListZeroFields />} />

          <Route
            path={routes.docs.cargoClaims}
            element={<CargoClaimsOverview />}
          />

          <Route
            path={routes.docs.cargoStatuses}
            element={<CargoClaimsStatuses />}
          />

          <Route
            path={routes.docs.cargoFields}
            element={<CargoClaimsFields />}
          />

          <Route
            path={routes.docs.healthClaims}
            element={<DocsComingSoon product="Health Claims" />}
          />
        </Route>

        <Route path={routes.listOne.index}>
          <Route index element={<ListOne />} />

          <Route
            path={routes.listOne.statuses}
            element={<ListOneStatusesListing />}
          />

          <Route path=':ticketId'>
            <Route index element={<ListOneTicketDetails />} />

            <Route path={routes.studies}>
              <Route index element={<ListOneTicketStudies />} />

              <Route path=':studyId' element={<ListOneTicketDetails />} />
            </Route>
          </Route>
        </Route>

        <Route path={routes.listTwo.index}>
          <Route index element={<ListTwo />} />

          <Route path=':ticketId'>
            <Route index element={<ListTwoTicketDetails />} />

            <Route path={routes.studies}>
              <Route index element={<ListTwoTicketStudies />} />

              <Route path=':studyId' element={<ListTwoTicketDetails />} />
            </Route>
          </Route>
        </Route>

        <Route path={routes.listThree.index}>
          <Route index element={<ListThree />} />

          <Route path=':id'>
            <Route index element={<ListThreeDetails />} />
          </Route>
        </Route>

        <Route path={routes.listFour.index} element={<ListFour />} />

        <Route path={routes.listFive.index} element={<ListFiveLayout />}>
          <Route
            path={routes.listFive.needApproval}
            element={<ListFiveNeedForApprovalListing />}
          />

          <Route
            path={routes.listFive.otherInvoices}
            element={<ListFiveOtherInvoicesListing />}
          />

          <Route
            path={routes.listFive.index}
            element={<Navigate replace to={routes.listFive.needApproval} />}
          />
        </Route>

        <Route path={routes.listSix.index}>
          <Route index element={<ListSix />} />

          <Route path=':id'>
            <Route index element={<ListSix />} />
          </Route>
        </Route>

        <Route path={routes.logs.index} element={<HistoryLogs />} />

        <Route path={routes.processedEvents.index}>
          <Route index element={<ProcessedEvents />} />

          <Route path=':status'>
            <Route index element={<ProcessedEvents />} />
          </Route>
        </Route>

        <Route path={routes.cargoClaims.index}>
          <Route index element={<CargoClaims />} />
          <Route
            path={routes.cargoClaims.details}
            element={<CargoClaimDetails />}
          />
        </Route>

        <Route path={routes.healthClaims.index}>
          <Route
            index
            element={
              <Navigate
                replace
                to={`${routes.healthClaims.index}/${routes.healthClaims.dashboard}`}
              />
            }
          />
          <Route
            path={routes.healthClaims.dashboard}
            element={<DirectClaims />}
          />
          <Route
            path={routes.healthClaims.closedTickets}
            element={<ClosedTickets />}
          />
          <Route
            path={routes.healthClaims.directClaims}
            element={
              <RedirectTo
                path={`${routes.healthClaims.index}/${routes.healthClaims.closedTickets}`}
              />
            }
          />
          <Route
            path={routes.healthClaims.reimbursementApproval}
            element={<GlobalApprovals />}
          />
          <Route path={routes.healthClaims.reimbursementClaims}>
            <Route
              index
              element={
                <Navigate replace to={routes.healthClaims.reimbursementAzadea} />
              }
            />
            <Route
              path={routes.healthClaims.reimbursementAzadea}
              element={<HealthClaims />}
            />
            <Route
              path={routes.healthClaims.reimbursementGlobal}
              element={<GlobalClaims />}
            />
          </Route>
          <Route
            path={routes.healthClaims.directBilling}
            element={<HealthClaims />}
          />
        </Route>

        <Route path={routes.role} element={<Role />} />

        <Route path={routes.admin} element={<Admin />} />

        <Route path={routes.authentication.index}>
          <Route path={routes.authentication.resetPin} element={<ResetPin />} />
          <Route
            path={routes.authentication.newPinReset}
            element={<NewPinResetForm />}
          />
          <Route path={routes.authentication.logout} element={<Logout />} />
        </Route>

        <Route path='*' element={<Navigate replace to={routes.main} />} />
      </Route>
    </Routes>
  );
}

export default function AuthorizedRoutes() {
  return <AzadeaRoutes />;
}
